import { ApiError } from "../errors.js";
import { AuthClient } from "./auth.js";
import { ClientBase } from "./base.js";
export class PublicClient extends AuthClient {
    /** Requests a presigned upload then uploads the file directly to the bucket. */
    async upload(file, filename, contentType) {
        const presigned = await this.createUpload(filename, contentType, file.size);
        const headers = {
            // Content-Type is signed into the URL (#266); send exactly what the server signed.
            "Content-Type": presigned.contentType ?? contentType,
        };
        // Cache-Control is signed (#735); omit only when older API responses lack it.
        if (presigned.cacheControl) {
            headers["Cache-Control"] = presigned.cacheControl;
        }
        const res = await this.fetchImpl(presigned.uploadUrl, {
            method: presigned.method,
            headers,
            body: file,
        });
        if (!res.ok) {
            throw new ApiError(res.status, "upload_failed", "direct upload to bucket failed");
        }
        await this.confirmUpload(presigned.key);
        return presigned;
    }
    /** Records upload_bytes after the bucket PUT succeeds. Minting a URL does not meter bytes. */
    async confirmUpload(key) {
        return this.request("POST", "/v1/uploads/confirm", { key }, true);
    }
    /**
     * Mints a presigned upload. `visibility: "private"` keeps the object off the public
     * bucket (read it back via getPrivateFileUrl); `purpose` is an optional server hint.
     */
    async createUpload(filename, contentType, contentLength, opts = {}) {
        const body = { filename, contentType, contentLength };
        if (opts.visibility !== undefined)
            body["visibility"] = opts.visibility;
        if (opts.purpose !== undefined)
            body["purpose"] = opts.purpose;
        return this.request("POST", "/v1/uploads", body, true);
    }
    /**
     * Short-lived signed read URL for a confirmed private file. Staff token of the
     * owning site; 404 for unknown/cross-tenant keys, 403 for non-uploaders without business:read.
     */
    async getPrivateFileUrl(slug, key) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/files/url${ClientBase.query({ key })}`, undefined, true);
    }
    /** Mints a marketing QR PNG or SVG; optional persist stores under tenants/{id}/qr/. */
    async generateQR(opts) {
        return this.request("POST", "/v1/qr", opts, true);
    }
    /** Files a feature request; the backend creates a GitHub issue server-side. */
    async createFeatureRequest(title, description) {
        return this.request("POST", "/v1/feature-requests", { title, description }, true);
    }
    /** Public contact form — POST /v1/contact (no auth). */
    async submitContact(input) {
        return this.request("POST", "/v1/contact", input);
    }
    /** Submits a structured sales lead from the public marketing form (no auth). */
    async submitLead(input) {
        return this.request("POST", "/v1/leads", input);
    }
    /** Presigns a direct-to-bucket upload for an optional lead-form image (no auth). */
    async presignLeadUpload(site, filename, contentType, contentLength) {
        return this.request("POST", "/v1/lead-uploads", {
            site,
            filename,
            contentType,
            contentLength,
        });
    }
    /** Admin: mint a Stripe Connect Express onboarding link for a tenant site. */
    async startConnectOnboarding(slug, returnUrl, refreshUrl) {
        return this.request("POST", `/v1/admin/sites/${encodeURIComponent(slug)}/connect/onboard`, {
            returnUrl,
            refreshUrl,
        }, true);
    }
    /** Tenant owner/manager: mint Connect AccountLink for their own site. */
    async startOwnerConnectOnboarding(slug, returnUrl, refreshUrl) {
        return this.request("POST", `/v1/sites/${encodeURIComponent(slug)}/connect/onboard`, {
            returnUrl,
            refreshUrl,
        }, true);
    }
    /** Tenant owner/manager: Stripe Connect state for their own site. */
    async getConnectStatus(slug) {
        return this.request("GET", `/v1/sites/${encodeURIComponent(slug)}/connect/status`, undefined, true);
    }
}
//# sourceMappingURL=public.js.map